import { Badge, BadgeProps } from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconClock,
  IconPlayerPlay,
  IconAlertTriangle,
  IconQuestionMark,
} from '@tabler/icons-react';

type StatusType = 'taskStatus' | 'driftSeverity' | 'identityConfidence' | 'dataQuality' | 'verificationStatus';

interface StatusConfig {
  color: string;
  label: string;
  icon?: React.ReactNode;
}

const statusConfigs: Record<StatusType, Record<string, StatusConfig>> = {
  taskStatus: {
    queued: { color: 'gray', label: 'Queued', icon: <IconClock size={12} /> },
    approved: { color: 'blue', label: 'Approved', icon: <IconCheck size={12} /> },
    scheduled: { color: 'cyan', label: 'Scheduled', icon: <IconClock size={12} /> },
    executing: { color: 'yellow', label: 'Executing', icon: <IconPlayerPlay size={12} /> },
    verifying: { color: 'violet', label: 'Verifying', icon: <IconClock size={12} /> },
    completed: { color: 'green', label: 'Completed', icon: <IconCheck size={12} /> },
    failed: { color: 'red', label: 'Failed', icon: <IconX size={12} /> },
    timeout: { color: 'orange', label: 'Timeout', icon: <IconAlertTriangle size={12} /> },
    cancelled: { color: 'gray', label: 'Cancelled', icon: <IconX size={12} /> },
  },
  driftSeverity: {
    none: { color: 'green', label: 'None' },
    low: { color: 'yellow', label: 'Low' },
    medium: { color: 'orange', label: 'Medium' },
    high: { color: 'red', label: 'High' },
  },
  identityConfidence: {
    high: { color: 'green', label: 'High' },
    medium: { color: 'yellow', label: 'Medium' },
    low: { color: 'red', label: 'Low' },
  },
  dataQuality: {
    good: { color: 'green', label: 'Good' },
    warning: { color: 'yellow', label: 'Warning' },
    poor: { color: 'red', label: 'Poor' },
  },
  verificationStatus: {
    passed: { color: 'green', label: 'Passed', icon: <IconCheck size={12} /> },
    failed: { color: 'red', label: 'Failed', icon: <IconX size={12} /> },
    pending: { color: 'gray', label: 'Pending', icon: <IconClock size={12} /> },
    timeout: { color: 'orange', label: 'Timeout', icon: <IconAlertTriangle size={12} /> },
  },
};

interface StatusBadgeProps extends Omit<BadgeProps, 'color'> {
  type: StatusType;
  value: string;
  showIcon?: boolean;
}

export function StatusBadge({ type, value, showIcon = true, ...props }: StatusBadgeProps) {
  const config = statusConfigs[type]?.[value] || {
    color: 'gray',
    label: value,
    icon: <IconQuestionMark size={12} />,
  };

  return (
    <Badge
      color={config.color}
      variant="light"
      leftSection={showIcon ? config.icon : undefined}
      {...props}
    >
      {config.label}
    </Badge>
  );
}
